import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { ColumnDef } from "@tanstack/react-table";
import TableContainer from "components/common/TableContainer";
import DeleteModal from "components/common/DeleteModal";
import { CheckBoxColumn } from "components/common/columns";
import { DateOnly } from "components/common/Date";
import type { Holiday } from "features/Holidays/types/holiday.types";
import {
  bulkDeleteHolidays,
  getHolidays,
} from "features/Holidays/services/HolidayService";
import {
  HolidayKind,
  HolidayKindLabel,
} from "features/Holidays/constants/holiday.constants";
import { toast } from "react-toastify";
import HolidayListFilters from "./HolidayListFilters";

const KIND_COLOR: Record<HolidayKind, string> = {
  [HolidayKind.PUBLIC]: "info",
  [HolidayKind.COMPANY]: "primary",
  [HolidayKind.OPTIONAL]: "warning",
};

function kindBadgeClass(k: HolidayKind) {
  return `bg-${KIND_COLOR[k] ?? "secondary"}-subtle text-${KIND_COLOR[k] ?? "secondary"}`;
}

type Props = {
  canUpdate: boolean;
  canDelete: boolean;
  onEdit: (holiday: Holiday) => void;
  onDelete: (holiday: Holiday) => void;
  refreshRef: React.MutableRefObject<(() => void) | null>;
};

export function HolidayListView({
  canUpdate,
  canDelete,
  onEdit,
  onDelete,
  refreshRef,
}: Props) {
  const tableRef = useRef<() => void>(null);
  const tableInstanceRef = useRef<any>(null);
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState<number | null>(null);
  const [selectedRows, setSelectedRows] = useState<Holiday[]>([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const requestParams = useMemo(() => ({ year, month }), [year, month]);

  useEffect(() => {
    refreshRef.current = () => tableRef.current?.();
  }, [refreshRef]);

  const handleBulkDelete = useCallback(async () => {
    if (!selectedRows.length) return;
    setBulkDeleting(true);
    try {
      const ids = selectedRows.map((r) => r.id);
      const res = await bulkDeleteHolidays(ids);
      if (res.success) {
        toast.success(
          `${(res.data as any)?.deleted ?? ids.length} holiday(s) deleted.`,
        );
        setSelectedRows([]);
        tableInstanceRef.current?.resetRowSelection();
        tableRef.current?.();
      } else {
        toast.error((res as any).message ?? "Bulk delete failed.");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Bulk delete failed.");
    } finally {
      setBulkDeleting(false);
      setBulkDeleteOpen(false);
    }
  }, [selectedRows]);

  const columns: ColumnDef<Holiday>[] = useMemo(
    () => [
      CheckBoxColumn<Holiday>(),
      {
        header: "Name",
        accessorKey: "name",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span className="fw-medium">{row.original.name}</span>
        ),
      },
      {
        header: "Date",
        accessorKey: "date",
        enableColumnFilter: false,
        cell: ({ row }) => <DateOnly date={row.original.date} />,
      },
      {
        header: "Kind",
        accessorKey: "kind",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const k = row.original.kind;
          return (
            <span className={`badge rounded-pill ${kindBadgeClass(k)}`}>
              {HolidayKindLabel[k]}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          if (!canUpdate && !canDelete)
            return <span className="text-muted small">—</span>;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Holiday actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                {canUpdate ? (
                  <DropdownItem onClick={() => onEdit(r)}>
                    <i className="ri-pencil-line align-bottom me-2 text-muted" />
                    Edit
                  </DropdownItem>
                ) : null}
                {canDelete ? (
                  <>
                    {canUpdate ? <DropdownItem divider /> : null}
                    <DropdownItem
                      className="text-danger"
                      onClick={() => onDelete(r)}
                    >
                      <i className="ri-delete-bin-line align-bottom me-2" />
                      Delete
                    </DropdownItem>
                  </>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [canDelete, canUpdate, onEdit, onDelete],
  );

  return (
    <>
      <DeleteModal
        show={bulkDeleteOpen}
        onDeleteClick={handleBulkDelete}
        onCloseClick={() => setBulkDeleteOpen(false)}
        recordId={`${selectedRows.length} holiday${selectedRows.length === 1 ? "" : "s"}`}
      />
      <div className="card-body pt-0">
        <TableContainer
          columns={columns}
          fetchApi={getHolidays}
          requestParams={requestParams}
          pageSize={10}
          globalSearch={false}
          refreshRef={tableRef}
          tableInstanceRef={tableInstanceRef}
          onSelectionChange={setSelectedRows}
          divClass="table-responsive table-card mb-0"
          tableClass="align-middle table-nowrap mb-0"
          theadClass="table-light text-muted"
          toolbarRight={
            <div className="d-flex gap-2 align-items-center flex-wrap">
              {canDelete && selectedRows.length > 0 ? (
                <Button
                  color="danger"
                  outline
                  size="sm"
                  disabled={bulkDeleting}
                  onClick={() => setBulkDeleteOpen(true)}
                >
                  <i className="ri-delete-bin-line align-bottom me-1" />
                  Delete selected ({selectedRows.length})
                </Button>
              ) : null}
              <HolidayListFilters
                year={year}
                onYearChange={setYear}
                month={month}
                onMonthChange={setMonth}
              />
            </div>
          }
          filterCount={0}
        />
      </div>
    </>
  );
}
